import { AssignStatus } from "@/enum/list.enum";

import { getReachOutStatus } from "@/pages/Private/pages/List/utils/columns/reachOutStatus";
import {
	CopywritingChannel,
	GenerateCopywritingResponse,
} from "@/pages/Private/pages/Messages/components/GenerateBar/types";
import { sequenceToDraftItems } from "@/pages/Private/pages/Messages/components/GenerateBar/sequenceToDraftItems.util";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";

/**
 * Which destinations a lead already belongs to — drives the "Add to …" → "Added to …" + check
 * flip in the Add-to menu. Membership for list/tracker/CRM is read from the prospect's join arrays
 * (present on the contact preview); campaign membership needs the resolved `ListPerson` (fetched
 * only when the lead is on a list), so it is derived separately in {@link isLeadInCampaign}.
 */
export interface LeadMembership {
	inList: boolean;
	inTracker: boolean;
	inCrm: boolean;
}

// Minimal structural shapes — the different surfaces (Contacts prospect, List `ListPerson.prospect`)
// carry these join arrays with different full types, but membership only needs a count (list/tracker)
// or the `crmType` (CRM). Keeping the inputs minimal lets every surface pass its arrays directly.
export type MembershipCountRecord = { id?: number | null };
export type MembershipCrmRecord = { crmType?: string | null };

export interface LeadMembershipSources {
	listPersons?: MembershipCountRecord[] | null;
	trackerPersons?: MembershipCountRecord[] | null;
	crmProspects?: MembershipCrmRecord[] | null;
	/** Which CRM provider the "Add to CRM" item targets (only its records count as membership). */
	crmType?: string;
}

/** Compute list / tracker / CRM membership from a prospect's join arrays. */
export const deriveLeadMembership = ({
	listPersons,
	trackerPersons,
	crmProspects,
	crmType = "hubspot",
}: LeadMembershipSources): LeadMembership => ({
	inList: (listPersons?.length ?? 0) > 0,
	inTracker: (trackerPersons?.length ?? 0) > 0,
	inCrm: (crmProspects ?? []).some(
		(record) => record.crmType?.toLowerCase() === crmType.toLowerCase()
	),
});

/**
 * Whether a lead's per-person campaign cut-off (`stopCampaignAt`) has passed — the same rule the
 * backend scheduler uses (`isCampaignStopped`). A paused lead has a `stopCampaignAt` at/in the past.
 */
export const isCampaignStopped = (
	stopCampaignAt?: string | null,
	now: Date = new Date()
): boolean => {
	if (!stopCampaignAt) {
		return false;
	}

	return new Date(stopCampaignAt).getTime() <= now.getTime();
};

/**
 * "Added to campaign" state for the Add-to menu: the lead has a campaign assigned (regardless of
 * whether it is currently sending).
 */
export const isLeadInCampaign = (listPerson?: ListPerson | null): boolean =>
	!!listPerson?.campaignId;

/**
 * Whether the lead is *actively* enrolled in a campaign **on this row** — a campaign is assigned and
 * its per-person cut-off has not passed. A paused (stopped) lead reads as not enrolled, so it falls
 * back to the default reach-out layout and can be re-engaged.
 *
 * This is the row-local answer, and it is only half the question: campaign membership is really
 * prospect-wide (see `resolveCampaignActions`, PREC-3906). Treating it as the whole answer is what
 * left a lead campaigning on another list looking untouched here.
 */
export const isLeadInActiveCampaign = (
	listPerson?: ListPerson | null,
	now: Date = new Date()
): boolean => isLeadInCampaign(listPerson) && !isCampaignStopped(listPerson?.stopCampaignAt, now);

/** The three mutually-exclusive shapes the primary reach-out button can take. */
export type ReachOutButtonKind = "reach-out" | "review-drafts" | "reply";

export interface ReachOutDescriptor {
	kind: ReachOutButtonKind;
	/** Number of ready AI drafts — shown as "Review drafts (n)". `0` when not a review-drafts state. */
	draftCount: number;
}

/**
 * Count the messages in a completed AI full-sequence draft, reusing the same parser the Chat tab
 * uses to build its draft list ({@link sequenceToDraftItems}). A mixed-channel sequence is filtered
 * to its entry-step channel — mirroring `buildFilteredAiDrafts`' no-history fallback — so the
 * number on the button matches the drafts the Chat tab will actually render. Returns `0` for
 * AI-snippet output or an unrecognised shape.
 */
export const countSequenceDrafts = (output?: GenerateCopywritingResponse | null): number => {
	const items = sequenceToDraftItems(output, CopywritingChannel.BOTH);

	if (items.length === 0) {
		return 0;
	}

	const entryChannel = items[0].channel;

	return items.filter((item) => item.channel === entryChannel).length;
};

/**
 * Map a resolved `ListPerson`'s generation/reply state to the primary reach-out button's shape,
 * reusing the shared {@link getReachOutStatus} state machine (reply → review-drafts → reach-out).
 * Snippet/generating states collapse to the plain "reach out" button — the panel always opens the
 * same Chat view, so only the label/icon differ.
 */
export const describeReachOut = (listPerson?: ListPerson | null): ReachOutDescriptor => {
	if (!listPerson) {
		return { kind: "reach-out", draftCount: 0 };
	}

	const status = getReachOutStatus({
		assignStatus: listPerson.assignStatus ?? null,
		copywritingStatus: listPerson.copywritingStatus ?? null,
		hasCopywritingOutput: !!listPerson.copywritingOutput,
	});

	if (status.state === "reply") {
		return { kind: "reply", draftCount: 0 };
	}

	if (status.state === "ready" && status.aiKind === "drafts") {
		return { kind: "review-drafts", draftCount: countSequenceDrafts(listPerson.copywritingOutput) };
	}

	return { kind: "reach-out", draftCount: 0 };
};

/** `AssignStatus.REPLIES` is the only state the shared helper treats as "awaiting a reply". */
export const isAwaitingReply = (listPerson?: ListPerson | null): boolean =>
	listPerson?.assignStatus === AssignStatus.REPLIES;
